// src/components/ErrorBoundary.js

import React from 'react';
import { getFriendlyErrorMessage } from '../utils/errorUtils';

/**
 * Error boundary component to catch and display errors in the component tree
 * This prevents the entire app from crashing when a component fails
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to the console
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Clear sessionChecked to prevent "Checking session..." from showing on error recovery
    try {
      sessionStorage.removeItem('sessionChecked');
    } catch (e) {
      // Ignore if sessionStorage is not available
    }
    
    // Optional: send the error to an error reporting service
    // reportErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Show a fallback UI with a way to retry/reset
      const friendlyMessage = getFriendlyErrorMessage(this.state.error);
      
      return (
        <div style={styles.container}>
          <div style={styles.errorCard}>
            <h1 style={styles.heading}>Something went wrong</h1>
            <p style={styles.message}>{friendlyMessage}</p>
            
            {this.props.showStackTrace && this.state.errorInfo && (
              <details style={styles.details}>
                <summary style={styles.summary}>View technical details</summary>
                <pre style={styles.stackTrace}>
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            
            <button
              style={styles.button}
              onClick={() => {
                // Reset the error state and try rendering again
                // Set sessionChecked to prevent loading screen
                try {
                  sessionStorage.setItem('sessionChecked', 'true');
                } catch (e) {
                  // Ignore if sessionStorage is not available
                }
                this.setState({ hasError: false, error: null, errorInfo: null });
              }}
            >
              Try Again
            </button>
            
            <button
              style={styles.secondaryButton}
              onClick={() => {
                // Full reset - clear all caches and reload
                try {
                  localStorage.clear();
                  sessionStorage.clear();
                } catch (e) {
                  // Ignore if storage is not available
                }
                window.location.href = '/';
              }}
            >
              Reset Application
            </button>
          </div>
        </div>
      );
    }

    // If there's no error, render the children normally
    return this.props.children;
  }
}

ErrorBoundary.defaultProps = {
  showStackTrace: process.env.NODE_ENV !== 'production',
  onReset: null
};

// Styles for the error UI
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '20px',
  },
  errorCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '30px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    maxWidth: '600px',
    width: '100%',
  },
  heading: {
    color: '#dc3545',
    marginTop: 0,
    fontSize: '24px',
  },
  message: {
    fontSize: '16px',
    lineHeight: 1.5,
    color: '#343a40',
    marginBottom: '20px',
  },
  details: {
    marginBottom: '20px',
  },
  summary: {
    cursor: 'pointer',
    color: '#6c757d',
    fontWeight: 'bold',
  },
  stackTrace: {
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '200px',
    fontSize: '14px',
    marginTop: '10px',
  },
  button: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
    marginRight: '10px',
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
  },
}; 